import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Search,
  Filter,
  X,
  Star,
  MapPin,
  Users,
  Heart,
  Send,
  Clock,
  DollarSign,
  CheckCircle2,
  AlertCircle,
  Plus,
  MessageCircle,
  Eye,
  Share2,
  Sparkles,
  Instagram,
  Youtube,
  Twitter,
  Linkedin,
  Globe,
  Calendar,
  Target,
  Award,
  ArrowRight,
} from 'lucide-react';
import api, { getErrorMessage } from '../../services/api';
import { useThemeStore } from '../../store/themeStore';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

interface Creator {
  id: string;
  displayName: string;
  avatar: string;
  verification: 'verified' | 'pending' | 'none';
  niches: string[];
  platforms: PlatformStats[];
  engagementRate: number;
  priceRange: { min: number; max: number };
  rating: number;
  reviewCount: number;
  location: string;
  bio: string;
  collaborationTypes: string[];
  portfolio?: string[];
  audienceDemographics?: AudienceDemographic[];
}

interface PlatformStats {
  platform: 'instagram' | 'youtube' | 'twitter' | 'tiktok' | 'linkedin';
  followers: number;
  engagementRate: number;
}

interface AudienceDemographic {
  label: string;
  value: number;
}

interface Campaign {
  id: string;
  title: string;
  // Backend field names
  campaign_type: string;
  status: string;
  description: string;
  target_niches: string[];
  target_platforms: string[];
  total_budget: number | null;
  total_applications: number;
  accepted_creators: number;
  application_deadline: string | null;
  content_deadline: string | null;
  deliverables: Deliverable[];
  currency: string;
  visibility: string;
  created_at: string;
  // Frontend aliases (for backward compat)
  type?: string;
  deadline?: string;
  budget?: number;
  niches?: string[];
  applicationsCount?: number;
  acceptedCreatorsCount?: number;
  targetAudience?: string[];
  platformRequirements?: string[];
  brands?: {
    logo: string;
    name: string;
  };
  createdAt?: string;
}

interface Deliverable {
  id: string;
  title: string;
  description: string;
  deadline: string;
  status: 'pending' | 'in_progress' | 'submitted' | 'approved' | 'rejected';
  creatorId?: string;
}

interface Proposal {
  id: string;
  campaignId: string;
  creatorId: string;
  status: 'pending' | 'accepted' | 'rejected' | 'negotiating' | 'completed';
  proposedPrice: number;
  timeline: string;
  coverLetter: string;
  createdAt: string;
  campaign?: Campaign;
  creator?: Creator;
  negotiationThread?: NegotiationMessage[];
}

interface NegotiationMessage {
  id: string;
  sender: 'creator' | 'brand';
  message: string;
  price?: number;
  timeline?: string;
  createdAt: string;
}

interface Review {
  id: string;
  reviewerId: string;
  rating: number;
  title: string;
  content: string;
  categories: {
    communication: number;
    delivery: number;
    professionalism: number;
  };
  createdAt: string;
  reviewer?: {
    displayName: string;
    avatar: string;
  };
}

interface FilterState {
  niches: string[];
  platforms: string[];
  followerRange: [number, number];
  engagementRange: [number, number];
  locations: string[];
  priceRange: [number, number];
  minRating: number;
  collaborationTypes: string[];
  verifiedOnly: boolean;
}

// ============================================================================
// PLATFORM ICON COMPONENT
// ============================================================================

const PlatformIcon: React.FC<{ platform: string; size?: number }> = ({ platform, size = 16 }) => {
  const iconProps = { size, className: 'text-gray-600' };
  switch (platform.toLowerCase()) {
    case 'instagram':
      return <Instagram {...iconProps} />;
    case 'youtube':
      return <Youtube {...iconProps} />;
    case 'twitter':
      return <Twitter {...iconProps} />;
    case 'tiktok':
      return <Globe {...iconProps} />;
    case 'linkedin':
      return <Linkedin {...iconProps} />;
    default:
      return <Globe {...iconProps} />;
  }
};

// ============================================================================
// RATING STARS COMPONENT
// ============================================================================

const RatingStars: React.FC<{ rating: number; size?: number; showNumber?: boolean }> = ({
  rating,
  size = 16,
  showNumber = true,
}) => (
  <div className="flex items-center gap-1">
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={size}
          className={star <= Math.round(rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
        />
      ))}
    </div>
    {showNumber && <span className="text-sm font-medium text-gray-700">{rating.toFixed(1)}</span>}
  </div>
);

// ============================================================================
// FILTER SIDEBAR
// ============================================================================

const FilterSidebar: React.FC<{
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  isOpen: boolean;
  onClose: () => void;
}> = ({ filters, onFilterChange, isOpen, onClose }) => {
  const { t } = useTranslation();
  const handleNicheChange = (niche: string) => {
    onFilterChange({
      ...filters,
      niches: filters.niches.includes(niche)
        ? filters.niches.filter((n) => n !== niche)
        : [...filters.niches, niche],
    });
  };

  const handleFollowerRangeChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const newRange: [number, number] = [...filters.followerRange];
    newRange[index] = parseInt(e.target.value);
    onFilterChange({ ...filters, followerRange: newRange });
  };

  const handleEngagementChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const newRange: [number, number] = [...filters.engagementRange];
    newRange[index] = parseFloat(e.target.value);
    onFilterChange({ ...filters, engagementRange: newRange });
  };

  const niches = ['Fashion', 'Beauty', 'Fitness', 'Tech', 'Food', 'Travel', 'Lifestyle', 'Gaming'];
  const platforms = ['Instagram', 'YouTube', 'TikTok', 'Twitter', 'LinkedIn'];
  const collaborationTypes = ['Sponsored Post', 'Product Placement', 'Brand Ambassador', 'Event'];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: -100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -100, opacity: 0 }}
            className="fixed left-0 top-0 h-full w-72 bg-white z-50 overflow-y-auto shadow-lg ring-1 ring-slate-200/50"
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-[13px] font-semibold text-slate-800">{t('mkt_filters', 'Filters')}</h3>
                <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg">
                  <X size={14} />
                </button>
              </div>

              {/* Niches */}
              <div className="mb-4">
                <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">{t('mkt_niches', 'Niches')}</h4>
                <div className="space-y-2">
                  {niches.map((niche) => (
                    <label key={niche} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.niches.includes(niche)}
                        onChange={() => handleNicheChange(niche)}
                        className="w-4 h-4 rounded border-gray-300"
                        id={`niche-${niche}`}
                      />
                      <span className="text-sm text-gray-700">{niche}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Platforms */}
              <div className="mb-4">
                <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">{t('mkt_platforms', 'Platforms')}</h4>
                <div className="space-y-2">
                  {platforms.map((platform) => (
                    <label key={platform} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.platforms.includes(platform)}
                        onChange={(e) =>
                          onFilterChange({
                            ...filters,
                            platforms: e.target.checked
                              ? [...filters.platforms, platform]
                              : filters.platforms.filter((p) => p !== platform),
                          })
                        }
                        className="w-4 h-4 rounded border-gray-300"
                        id={`platform-${platform}`}
                      />
                      <span className="text-sm text-gray-700">{platform}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Follower Range */}
              <div className="mb-4">
                <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">{t('mkt_follower_range', 'Follower Range')}</h4>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="range"
                      min="0"
                      max="10000000"
                      step="10000"
                      value={filters.followerRange[0]}
                      onChange={(e) => handleFollowerRangeChange(e, 0)}
                      aria-label="Minimum followers"
                      className="flex-1"
                    />
                  </div>
                  <div className="text-xs text-gray-500">
                    {filters.followerRange[0].toLocaleString()} - {filters.followerRange[1].toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Engagement Rate */}
              <div className="mb-4">
                <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">{t('mkt_engagement_rate', 'Engagement Rate')}</h4>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="0.1"
                      value={filters.engagementRange[0]}
                      onChange={(e) => handleEngagementChange(e, 0)}
                      aria-label="Minimum engagement rate"
                      className="flex-1"
                    />
                  </div>
                  <div className="text-xs text-gray-500">
                    {filters.engagementRange[0].toFixed(1)}% - {filters.engagementRange[1].toFixed(1)}%
                  </div>
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-4">
                <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">{t('mkt_price_range', 'Price Range (per post)')}</h4>
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="range"
                      min="0"
                      max="50000"
                      step="500"
                      value={filters.priceRange[0]}
                      onChange={(e) => {
                        const newRange: [number, number] = [...filters.priceRange];
                        newRange[0] = parseInt(e.target.value);
                        onFilterChange({ ...filters, priceRange: newRange });
                      }}
                      aria-label="Minimum price"
                      className="flex-1"
                    />
                  </div>
                  <div className="text-xs text-gray-500">
                    ${filters.priceRange[0].toLocaleString()} - ${filters.priceRange[1].toLocaleString()}
                  </div>
                </div>
              </div>

              {/* Min Rating */}
              <div className="mb-4">
                <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">{t('mkt_min_rating', 'Minimum Rating')}</h4>
                <select
                  value={filters.minRating}
                  onChange={(e) => onFilterChange({ ...filters, minRating: parseFloat(e.target.value) })}
                  aria-label={t('mkt_min_rating_aria', 'Minimum rating')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
                >
                  <option value="0">{t('mkt_any_rating', 'Any rating')}</option>
                  <option value="4">{t('mkt_4_stars', '4+ stars')}</option>
                  <option value="4.5">{t('mkt_4_5_stars', '4.5+ stars')}</option>
                  <option value="5">{t('mkt_5_stars', '5 stars')}</option>
                </select>
              </div>

              {/* Collaboration Types */}
              <div className="mb-4">
                <h4 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-2">{t('mkt_collab_types', 'Collaboration Types')}</h4>
                <div className="space-y-2">
                  {collaborationTypes.map((type) => (
                    <label key={type} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.collaborationTypes.includes(type)}
                        onChange={(e) =>
                          onFilterChange({
                            ...filters,
                            collaborationTypes: e.target.checked
                              ? [...filters.collaborationTypes, type]
                              : filters.collaborationTypes.filter((t) => t !== type),
                          })
                        }
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className="text-sm text-gray-700">{type}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Verified Only */}
              <div className="mb-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.verifiedOnly}
                    onChange={(e) => onFilterChange({ ...filters, verifiedOnly: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300"
                    id="verifiedOnly"
                  />
                  <span className="text-sm font-medium text-gray-700">{t('mkt_verified_only', 'Verified creators only')}</span>
                </label>
              </div>

              {/* Reset Filters */}
              <button
                onClick={() => {
                  onFilterChange({
                    niches: [],
                    platforms: [],
                    followerRange: [0, 10000000],
                    engagementRange: [0, 100],
                    locations: [],
                    priceRange: [0, 50000],
                    minRating: 0,
                    collaborationTypes: [],
                    verifiedOnly: false,
                  });
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t('mkt_reset_filters', 'Reset Filters')}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// ============================================================================
// CREATOR CARD COMPONENT
// ============================================================================

const CreatorCard: React.FC<{
  creator: Creator;
  onViewProfile: (creator: Creator) => void;
  onSendProposal: (creator: Creator) => void;
}> = ({ creator, onViewProfile, onSendProposal }) => {
  const { t } = useTranslation();
  return (
  <div
    className="group bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/50 overflow-hidden hover:ring-slate-300 transition-all"
  >
    {/* Header Accent */}
    <div className="relative h-20 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/30 to-blue-600/30 opacity-40" />

        <div className="absolute top-3 right-3 flex gap-1.5">
            <button className="p-1.5 bg-white/10 backdrop-blur-md rounded-lg text-white hover:bg-white hover:text-red-500 transition-all">
                <Heart size={14} />
            </button>
            <button className="p-1.5 bg-white/10 backdrop-blur-md rounded-lg text-white hover:bg-white hover:text-slate-900 transition-all">
                <Share2 size={14} />
            </button>
        </div>

        {creator.verification === 'verified' && (
            <div className="absolute bottom-2.5 right-3 px-2 py-0.5 bg-emerald-500 rounded-full text-[10px] font-semibold text-white uppercase tracking-wider flex items-center gap-1">
                <CheckCircle2 size={10} />
                {t('mkt_verified', 'Verified')}
            </div>
        )}
    </div>

    {/* Avatar & Basic Info */}
    <div className="px-4 pb-4 pt-0 relative">
      <div className="flex items-end gap-3 -mt-6 mb-3 relative z-10">
        <img
          src={creator.avatar}
          alt={creator.displayName}
          className="w-14 h-14 rounded-xl border-[3px] border-white object-cover shadow-sm"
        />
        <div className="flex-1 mb-1">
          <h3 className="text-sm font-semibold text-slate-900 leading-tight mb-0.5 group-hover:text-emerald-600 transition-colors">
            {creator.displayName}
          </h3>
          <div className="flex items-center gap-1 text-slate-500">
            <MapPin size={10} className="text-slate-400" />
            <span className="text-[11px]">{creator.location}</span>
          </div>
        </div>
      </div>

      {/* Bio */}
      <p className="text-xs text-slate-600 leading-relaxed line-clamp-2 mb-3">
        {creator.bio}
      </p>

      {/* Platform Stats */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        {creator.platforms.slice(0, 2).map((platform) => (
          <div key={platform.platform} className="p-2 bg-slate-50/80 rounded-lg ring-1 ring-slate-100">
            <div className="flex items-center justify-between mb-1">
              <PlatformIcon platform={platform.platform} size={12} />
              <span className="text-[10px] font-semibold text-emerald-600">
                {platform.engagementRate.toFixed(1)}%
              </span>
            </div>
            <p className="text-sm font-semibold text-slate-900">
                {(platform.followers / 1000).toFixed(0)}K
            </p>
            <p className="text-[10px] text-slate-400 capitalize">
                {platform.platform}
            </p>
          </div>
        ))}
      </div>

      {/* Key Metrics Row */}
      <div className="flex items-center justify-between py-2.5 border-y border-slate-100/80 mb-3">
        <div className="text-center flex-1">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">{t('mkt_rating', 'Rating')}</p>
          <div className="flex justify-center">
            <RatingStars rating={creator.rating} size={12} showNumber={true} />
          </div>
        </div>
        <div className="w-px h-6 bg-slate-100" />
        <div className="text-center flex-1">
          <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">{t('mkt_price', 'Price')}</p>
          <p className="text-sm font-semibold text-slate-900">${creator.priceRange.min}K+</p>
        </div>
      </div>

      {/* Niches */}
      <div className="flex flex-wrap gap-1 mb-4">
        {creator.niches.slice(0, 3).map((niche) => (
          <span key={niche} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-semibold rounded-full uppercase tracking-wide">
            {niche}
          </span>
        ))}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          onClick={() => onViewProfile(creator)}
          className="flex-1 py-2 px-3 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-all flex items-center justify-center gap-1.5"
        >
          <Eye size={13} />
          {t('mkt_profile', 'View Details')}
        </button>
        <button
          onClick={() => onSendProposal(creator)}
          className="flex-1 py-2 px-3 bg-slate-900 text-white rounded-xl text-xs font-semibold hover:bg-emerald-600 transition-all shadow-sm flex items-center justify-center gap-1.5"
        >
          <Send size={13} />
          {t('mkt_propose', 'Collaborate')}
        </button>
      </div>
    </div>
  </div>
  );
};

// ============================================================================
// CREATOR PROFILE MODAL
// ============================================================================

const CreatorProfileModal: React.FC<{
  creator: Creator | null;
  isOpen: boolean;
  onClose: () => void;
  onSendProposal: (creator: Creator) => void;
}> = ({ creator, isOpen, onClose, onSendProposal }) => {
  const { t } = useTranslation();
  if (!creator) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 z-40"
            onClick={onClose}
          />
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="fixed inset-4 bg-white rounded-2xl z-50 overflow-y-auto max-w-4xl mx-auto shadow-lg ring-1 ring-slate-200/50"
          >
            <div className="relative">
              <button
                onClick={onClose}
                className="absolute top-3 right-3 p-1.5 bg-white rounded-lg border border-slate-200 hover:bg-slate-50 z-10"
              >
                <X size={16} />
              </button>

              <div className="h-20 bg-slate-900" />

              <div className="px-5 pb-5">
                <div className="flex items-end gap-4 -mt-10 mb-4">
                  <img
                    src={creator.avatar}
                    alt={creator.displayName}
                    className="w-20 h-20 rounded-xl border-[3px] border-white object-cover shadow-sm"
                  />
                  <div className="flex-1 pb-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h1 className="text-lg font-semibold text-slate-900">{creator.displayName}</h1>
                      {creator.verification === 'verified' && (
                        <CheckCircle2 size={16} className="text-emerald-500" />
                      )}
                    </div>
                    <p className="text-xs text-slate-600 mb-1">{creator.bio}</p>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <span className="flex items-center gap-1"><MapPin size={12} />{creator.location}</span>
                      <RatingStars rating={creator.rating} size={12} />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-5 p-3 bg-slate-50/80 rounded-xl ring-1 ring-slate-100">
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{t('mkt_engagement_rate', 'Engagement Rate')}</p>
                    <p className="text-lg font-semibold text-slate-900">{creator.engagementRate.toFixed(1)}%</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{t('mkt_total_followers', 'Total Followers')}</p>
                    <p className="text-lg font-semibold text-slate-900">
                      {(creator.platforms.reduce((sum, p) => sum + p.followers, 0) / 1000).toFixed(0)}K
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{t('mkt_reviews_label', 'Reviews')}</p>
                    <p className="text-lg font-semibold text-slate-900">{creator.reviewCount}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{t('mkt_pricing', 'Pricing')}</p>
                    <p className="text-lg font-semibold text-slate-900">
                      ${creator.priceRange.min}K - ${creator.priceRange.max}K
                    </p>
                  </div>
                </div>

                <div className="mb-5">
                  <h2 className="text-[13px] font-semibold text-slate-800 mb-2">{t('mkt_platform_presence', 'Platform Presence')}</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {creator.platforms.map((platform) => (
                      <div key={platform.platform} className="p-3 bg-slate-50/80 rounded-xl ring-1 ring-slate-100">
                        <div className="flex items-center gap-2 mb-2">
                          <PlatformIcon platform={platform.platform} size={14} />
                          <span className="text-xs font-semibold text-slate-900 capitalize">{platform.platform}</span>
                        </div>
                        <div className="space-y-0.5 text-xs">
                          <div className="flex justify-between">
                            <span className="text-slate-500">{t('mkt_followers', 'Followers')}</span>
                            <span className="font-semibold text-slate-900">{(platform.followers / 1000).toFixed(0)}K</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">{t('mkt_engagement', 'Engagement')}</span>
                            <span className="font-semibold text-emerald-600">{platform.engagementRate.toFixed(1)}%</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5 mb-5">
                  <div>
                    <h2 className="text-[13px] font-semibold text-slate-800 mb-2">{t('mkt_niches', 'Niches')}</h2>
                    <div className="flex flex-wrap gap-1.5">
                      {creator.niches.map((niche) => (
                        <span key={niche} className="px-2.5 py-1 bg-emerald-50 text-emerald-700 text-[11px] font-semibold rounded-full">
                          {niche}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h2 className="text-[13px] font-semibold text-slate-800 mb-2">{t('mkt_collab_types', 'Collaboration Types')}</h2>
                    <div className="flex flex-wrap gap-1.5">
                      {creator.collaborationTypes.map((type) => (
                        <span key={type} className="px-2.5 py-1 bg-purple-50 text-purple-700 text-[11px] font-semibold rounded-full">
                          {type}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {creator.audienceDemographics && creator.audienceDemographics.length > 0 && (
                  <div className="mb-5">
                    <h2 className="text-[13px] font-semibold text-slate-800 mb-2">{t('mkt_audience_demo', 'Audience Demographics')}</h2>
                    <div className="space-y-2">
                      {creator.audienceDemographics.map((demo) => (
                        <div key={demo.label}>
                          <div className="flex justify-between text-xs mb-0.5">
                            <span className="text-slate-500">{demo.label}</span>
                            <span className="font-semibold text-slate-900">{demo.value}%</span>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-1.5">
                            <div
                              className="bg-emerald-500 h-1.5 rounded-full"
                              style={{ width: `${demo.value}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {creator.portfolio && creator.portfolio.length > 0 && (
                  <div className="mb-5">
                    <h2 className="text-[13px] font-semibold text-slate-800 mb-2">{t('mkt_portfolio', 'Portfolio')}</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {creator.portfolio.map((item, idx) => (
                        <div key={idx} className="aspect-square bg-slate-100 rounded-xl overflow-hidden">
                          <img src={item} alt={`Portfolio ${idx}`} className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => {
                    onSendProposal(creator);
                    onClose();
                  }}
                  className="w-full py-2.5 px-4 bg-slate-900 text-white text-[13px] font-semibold rounded-xl hover:bg-emerald-600 transition-all shadow-sm flex items-center justify-center gap-2"
                >
                  <Send size={14} />
                  {t('mkt_send_proposal', 'Send Collaboration Proposal')}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// ============================================================================
// PROPOSAL FORM MODAL
// ============================================================================

const ProposalFormModal: React.FC<{
  creator: Creator | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (proposal: Partial<Proposal>) => Promise<void>;
}> = ({ creator, isOpen, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    proposedPrice: '',
    timeline: '',
    coverLetter: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!creator) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        creatorId: creator.id,
        proposedPrice: parseFloat(formData.proposedPrice),
        timeline: formData.timeline,
        coverLetter: formData.coverLetter,
      });
      setFormData({ proposedPrice: '', timeline: '', coverLetter: '' });
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && creator && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/30 z-40"
            onClick={onClose}
          />
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="fixed inset-4 bg-white rounded-2xl z-50 max-w-2xl mx-auto shadow-lg ring-1 ring-slate-200/50"
          >
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <img
                    src={creator.avatar}
                    alt={creator.displayName}
                    className="w-10 h-10 rounded-xl"
                  />
                  <div>
                    <h2 className="text-[13px] font-semibold text-slate-900">{t('mkt_send_proposal_modal', 'Send Proposal')}</h2>
                    <p className="text-[11px] text-slate-500">{t('mkt_to', 'to')} {creator.displayName}</p>
                  </div>
                </div>
                <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
                  <X size={16} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <span className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                    <DollarSign size={11} className="inline mr-1" />
                    {t('mkt_proposed_price', 'Proposed Price ($)')}
                  </span>
                  <input
                    type="number"
                    value={formData.proposedPrice}
                    onChange={(e) => setFormData({ ...formData, proposedPrice: e.target.value })}
                    placeholder={t('mkt_enter_offer', 'Enter your offer')}
                    className="w-full px-3 py-2 text-sm bg-slate-50/80 border-0 ring-1 ring-slate-200/60 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/20 placeholder-slate-400"
                    required
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    {t('mkt_creator_rate', 'Creator&apos;s rate')}: ${creator.priceRange.min}K - ${creator.priceRange.max}K
                  </p>
                </div>

                <div>
                  <span className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                    <Calendar size={11} className="inline mr-1" />
                    {t('mkt_timeline', 'Timeline')}
                  </span>
                  <input
                    type="text"
                    value={formData.timeline}
                    onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                    placeholder={t('mkt_timeline_placeholder', 'e.g., 2 weeks for content creation')}
                    className="w-full px-3 py-2 text-sm bg-slate-50/80 border-0 ring-1 ring-slate-200/60 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/20 placeholder-slate-400"
                    required
                  />
                </div>

                <div>
                  <span className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                    <MessageCircle size={11} className="inline mr-1" />
                    {t('mkt_cover_letter', 'Cover Letter')}
                  </span>
                  <textarea
                    value={formData.coverLetter}
                    onChange={(e) => setFormData({ ...formData, coverLetter: e.target.value })}
                    placeholder={t('mkt_cover_letter_placeholder', 'Tell them why you think this partnership is perfect...')}
                    rows={5}
                    className="form-textarea w-full px-3 py-2 text-sm bg-slate-50/80 border-0 ring-1 ring-slate-200/60 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/20 resize-none placeholder-slate-400"
                    required
                  />
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 transition"
                  >
                    {t('mkt_cancel', 'Cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-2.5 bg-slate-900 text-white rounded-xl text-xs font-semibold hover:bg-emerald-600 transition disabled:opacity-50"
                  >
                    {isSubmitting ? t('mkt_sending', 'Sending...') : t('mkt_send_proposal_btn', 'Send Proposal')}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// ============================================================================
// DISCOVER CREATORS TAB
// ============================================================================

const DiscoverCreatorsTab: React.FC<{ searchQuery: string }> = ({ searchQuery }) => {
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState<'relevance' | 'followers' | 'engagement' | 'price' | 'rating'>(
    'relevance'
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState<Creator | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [proposalCreator, setProposalCreator] = useState<Creator | null>(null);
  const [proposalOpen, setProposalOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    niches: [],
    platforms: [],
    followerRange: [0, 10000000],
    engagementRange: [0, 100],
    locations: [],
    priceRange: [0, 50000],
    minRating: 0,
    collaborationTypes: [],
    verifiedOnly: false,
  });

  // Fetch creators
  const { data: creators = [], isLoading, error } = useQuery({
    queryKey: ['creators', searchQuery, sortBy, filters],
    queryFn: async () => {
      // Map frontend filter state to backend query params
      const params: Record<string, unknown> = {
        sort_by: sortBy === 'price' ? 'rate' : sortBy,
      };
      if (searchQuery) { params.search = searchQuery; }
      if (filters.niches.length > 0) { params.niche = filters.niches; }
      if (filters.platforms.length > 0) { params.platform = filters.platforms[0]; }
      if (filters.followerRange[0] > 0) { params.min_followers = filters.followerRange[0]; }
      if (filters.engagementRange[0] > 0) { params.min_engagement = filters.engagementRange[0]; }
      if (filters.locations.length > 0) { params.location = filters.locations[0]; }
      if (filters.priceRange[0] > 0) { params.min_rate = filters.priceRange[0]; }
      if (filters.priceRange[1] < 50000) { params.max_rate = filters.priceRange[1]; }

      const response = await api.get('/marketplace/creators', { params });
      return (response.data as { items?: unknown[] }).items || response.data || [];
    },
  });

  // Fetch recommendations
  const { data: recommendations = [] } = useQuery({
    queryKey: ['creator-recommendations'],
    queryFn: async () => {
      const response = await api.get('/marketplace/creators/recommendations');
      return (response.data as { items?: unknown[] }).items || response.data || [];
    },
  });

  // Send proposal mutation
  const sendProposalMutation = useMutation({
    mutationFn: async (proposal: Partial<Proposal>) => {
      const response = await api.post('/marketplace/campaigns/proposals', proposal);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Proposal sent successfully!');
      setProposalOpen(false);
      setProposalCreator(null);
    },
    onError: () => {
      toast.error('Failed to send proposal');
    },
  });

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
            <button
              onClick={() => setFilterOpen(!filterOpen)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-full transition-all text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            >
              <Filter size={12} />
              {t('mkt_filters', 'Filters')}
              {filters.niches.length + filters.platforms.length > 0 && (
                <span className="w-4 h-4 rounded-full bg-slate-900 text-white text-[9px] flex items-center justify-center">
                    {filters.niches.length + filters.platforms.length}
                </span>
              )}
            </button>

            <div className="h-4 w-px bg-slate-200" />

            <div className="flex items-center gap-1.5">
                <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{t('mkt_sort', 'Sort')}:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                  aria-label={t('mkt_sort_by_aria', 'Sort by')}
                  className="bg-transparent border-none focus:ring-0 text-xs font-medium text-slate-900 cursor-pointer p-0 pr-5"
                >
                  <option value="relevance">{t('mkt_sort_relevance', 'Relevance')}</option>
                  <option value="followers">{t('mkt_sort_followers', 'Followers')}</option>
                  <option value="engagement">{t('mkt_sort_engagement', 'Engagement')}</option>
                  <option value="price">{t('mkt_sort_price', 'Price')}</option>
                  <option value="rating">{t('mkt_sort_rating', 'Rating')}</option>
                </select>
            </div>
        </div>

        <span className="text-[11px] text-slate-400">
            {creators.length} {t('mkt_results_found', 'creators found')}
        </span>
      </div>

      <FilterSidebar
        filters={filters}
        onFilterChange={setFilters}
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
      />

      {/* AI Recommendations */}
      {recommendations.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/50 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-emerald-500" />
              <span className="text-[13px] font-semibold text-slate-800">{t('mkt_ai_recommendations', 'AI Top Picks')}</span>
              <span className="text-[11px] text-slate-400">{t('mkt_ai_subtitle', 'Matched to your brand')}</span>
            </div>
            <button className="text-[11px] font-semibold text-slate-500 hover:text-slate-700 transition">
                {t('mkt_view_all_matches', 'View all')}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {recommendations.slice(0, 3).map((creator: Creator) => (
              <div
                key={creator.id}
                role="button"
                tabIndex={0}
                className="p-3 bg-slate-50/80 rounded-xl ring-1 ring-slate-100 cursor-pointer hover:ring-slate-200 transition-all group"
                onClick={() => {
                    setSelectedCreator(creator);
                    setProfileOpen(true);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedCreator(creator);
                    setProfileOpen(true);
                  }
                }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="relative">
                    <img src={creator.avatar} alt={creator.displayName} className="w-9 h-9 rounded-lg object-cover" />
                    <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-emerald-500 rounded text-[8px] font-semibold text-white flex items-center justify-center border border-white">
                      98
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-900 truncate group-hover:text-emerald-600 transition-colors">{creator.displayName}</p>
                    <RatingStars rating={creator.rating} size={10} showNumber={false} />
                  </div>
                  <ArrowRight size={12} className="text-slate-300 group-hover:text-emerald-500 transition-colors flex-shrink-0" />
                </div>
                <div className="flex gap-1 overflow-hidden">
                  {creator.niches.slice(0, 2).map((niche) => (
                    <span key={niche} className="px-1.5 py-0.5 bg-white text-[9px] font-semibold text-slate-500 rounded uppercase">
                      {niche}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-slate-100 rounded-2xl h-72 animate-pulse" />
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 rounded-xl ring-1 ring-red-200 flex items-center gap-2 text-xs">
          <AlertCircle className="text-red-500" size={14} />
          <span className="text-red-700">{t('mkt_load_error', 'Failed to load creators. Please try again.')}</span>
        </div>
      )}

      {/* Creator Grid */}
      {!isLoading && creators.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {creators.map((creator: Creator) => (
            <CreatorCard
              key={creator.id}
              creator={creator}
              onViewProfile={(c) => {
                setSelectedCreator(c);
                setProfileOpen(true);
              }}
              onSendProposal={(c) => {
                setProposalCreator(c);
                setProposalOpen(true);
              }}
            />
          ))}
        </div>
      )}

      {/* Empty */}
      {!isLoading && creators.length === 0 && (
        <div className="text-center py-12 bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/50">
          <Users size={32} className="mx-auto text-slate-300 mb-3" />
          <h3 className="text-[13px] font-semibold text-slate-800 mb-1">{t('mkt_no_creators', 'No creators found')}</h3>
          <p className="text-xs text-slate-500">{t('mkt_adjust_filters', 'Try adjusting your filters or search terms')}</p>
        </div>
      )}

      {/* Modals */}
      <CreatorProfileModal
        creator={selectedCreator}
        isOpen={profileOpen}
        onClose={() => {
          setProfileOpen(false);
          setSelectedCreator(null);
        }}
        onSendProposal={(c) => {
          setProposalCreator(c);
          setProposalOpen(true);
        }}
      />

      <ProposalFormModal
        creator={proposalCreator}
        isOpen={proposalOpen}
        onClose={() => {
          setProposalOpen(false);
          setProposalCreator(null);
        }}
        onSubmit={(proposal) => sendProposalMutation.mutateAsync(proposal)}
      />
    </div>
  );
};

// ============================================================================
// MY CAMPAIGNS TAB
// ============================================================================

const MyCampaignsTab: React.FC = () => {
  const { t } = useTranslation();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    campaign_type: 'sponsored_post',
    budget: 0,
    deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    niches: [] as string[],
    deliverables_description: '',
  });

  // Fetch campaigns
  const { data: campaigns = [], isLoading, refetch } = useQuery({
    queryKey: ['my-campaigns'],
    queryFn: async () => {
      const response = await api.get('/marketplace/campaigns');
      return (response.data as { items?: unknown[] }).items || response.data || [];
    },
  });

  // Create campaign mutation
  const createCampaignMutation = useMutation({
    mutationFn: async (campaign: Partial<Campaign>) => {
      const response = await api.post('/marketplace/campaigns', campaign);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Campaign created successfully!');
      setCreateModalOpen(false);
      setStep(1);
      refetch();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to create campaign'));
    },
  });

  const getStatusColor = (status: Campaign['status']) => {
    switch (status) {
      case 'live':
        return 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20';
      case 'draft':
        return 'bg-slate-200 text-slate-700';
      case 'closed':
        return 'bg-amber-500 text-white shadow-lg shadow-amber-500/20';
      case 'completed':
        return 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      sponsored_post: 'bg-purple-50 text-purple-700 border-purple-200',
      product_placement: 'bg-pink-50 text-pink-700 border-pink-200',
      brand_ambassador: 'bg-orange-50 text-orange-700 border-orange-200',
      event: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    };
    return colors[type] || 'bg-slate-50 text-slate-700 border-slate-200';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
            <p className="text-[13px] text-slate-500">{t('mkt_my_campaigns_subtitle', 'Manage and track your active influencer collaborations')}</p>
        </div>
        <button
          onClick={() => setCreateModalOpen(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-900 text-white rounded-full text-[11px] font-semibold hover:bg-emerald-600 transition-all shadow-sm"
        >
          <Plus size={12} />
          {t('mkt_create_campaign', 'Create Campaign')}
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-slate-100 rounded-2xl h-36 animate-pulse" />
          ))}
        </div>
      )}

      {/* Campaign Cards */}
      {!isLoading && campaigns.length > 0 && (
        <div className="space-y-3">
          {campaigns.map((campaign: Campaign) => (
            <div
              key={campaign.id}
              role="button"
              tabIndex={0}
              onClick={() => {
                setSelectedCampaign(campaign);
                setDetailOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedCampaign(campaign);
                  setDetailOpen(true);
                }
              }}
              className="group bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/50 hover:ring-slate-300 transition-all cursor-pointer overflow-hidden"
            >
              <div className="p-4 flex flex-col lg:flex-row items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="text-sm font-semibold text-slate-900 group-hover:text-emerald-600 transition-colors">{campaign.title}</h3>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${getTypeColor(campaign.campaign_type || '')}`}>
                      {(campaign.campaign_type || '').replace(/_/g, ' ')}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${getStatusColor(campaign.status)}`}>
                      {campaign.status}
                    </span>
                  </div>
                  <p className="text-xs text-slate-600 leading-relaxed max-w-2xl mb-3 line-clamp-2">{campaign.description}</p>

                  <div className="flex flex-wrap gap-1">
                    {(campaign.target_niches || []).slice(0, 4).map((niche) => (
                      <span key={niche} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-semibold rounded-full uppercase">
                        {niche}
                      </span>
                    ))}
                    {campaign.target_niches?.length > 4 && (
                      <span className="px-2 py-0.5 bg-slate-50 text-slate-400 text-[10px] rounded-full">
                        +{campaign.target_niches.length - 4}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 min-w-[180px]">
                    <div className="p-3 bg-slate-50/80 rounded-xl ring-1 ring-slate-100">
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">{t('mkt_campaign_budget', 'Budget')}</p>
                        <p className="text-lg font-semibold text-slate-900">${((campaign.total_budget ?? 0) / 1000).toFixed(0)}K</p>
                    </div>
                    <div className="text-center p-2">
                        <p className="text-[10px] font-semibold text-slate-400 uppercase mb-0.5">{t('mkt_apps', 'Apps')}</p>
                        <p className="text-sm font-semibold text-slate-900">{campaign.total_applications ?? 0}</p>
                    </div>
                    <div className="text-center p-2">
                        <p className="text-[10px] font-semibold text-slate-400 uppercase mb-0.5">{t('mkt_hired', 'Hired')}</p>
                        <p className="text-sm font-semibold text-emerald-600">{campaign.accepted_creators ?? 0}</p>
                    </div>
                </div>
              </div>

              <div className="px-4 py-2.5 border-t border-slate-100/80 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
                        <Calendar size={11} className="text-slate-400" />
                        {t('mkt_deadline', 'Deadline')}: {campaign.application_deadline ? new Date(campaign.application_deadline).toLocaleDateString() : 'N/A'}
                    </span>
                    <span className="hidden sm:flex items-center gap-1.5 text-[11px] text-slate-500">
                        <Target size={11} className="text-slate-400" />
                        <span className="flex gap-0.5">
                            {(campaign.target_platforms || []).map(p => (
                                <PlatformIcon key={p} platform={p} size={11} />
                            ))}
                        </span>
                    </span>
                </div>
                <span className="flex items-center gap-1 text-[11px] font-semibold text-emerald-600">
                    {t('mkt_view_performance', 'Manage')}
                    <ArrowRight size={11} className="group-hover:translate-x-0.5 transition-transform" />
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty */}
      {!isLoading && campaigns.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/50">
          <Target size={32} className="text-slate-300 mb-3" />
          <h3 className="text-[13px] font-semibold text-slate-800 mb-1">{t('mkt_no_campaigns', 'No campaigns yet')}</h3>
          <p className="text-xs text-slate-500 mb-4 max-w-xs text-center">{t('mkt_create_first_campaign', 'Start your journey by creating a campaign and connecting with top creators.')}</p>
          <button
            onClick={() => setCreateModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-slate-900 text-white rounded-full text-[13px] font-semibold hover:bg-emerald-600 transition-all shadow-sm"
          >
            {t('mkt_create_campaign_cta', 'Get Started Now')}
          </button>
        </div>
      )}

      {/* Create Campaign Modal */}
      <AnimatePresence>
        {createModalOpen && (
          <div
            role="button"
            tabIndex={0}
            aria-label="Close"
            className="fixed inset-0 z-50 flex items-center justify-center"
            onClick={() => setCreateModalOpen(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setCreateModalOpen(false);
              }
            }}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/30"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-[95vw] max-w-lg max-h-[80vh] overflow-y-auto bg-white rounded-2xl shadow-lg ring-1 ring-slate-200/50"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-[13px] font-semibold text-slate-900">{t('mkt_create_campaign_modal', 'Create Campaign')}</h2>
                  <button
                    onClick={() => {
                      setCreateModalOpen(false);
                      setStep(1);
                    }}
                    className="p-1.5 hover:bg-slate-100 rounded-lg"
                  >
                    <X size={16} />
                  </button>
                </div>

                {/* Progress Indicator */}
                <div className="flex gap-1 mb-5">
                  {[1, 2, 3, 4, 5, 6].map((s) => (
                    <div
                      key={s}
                      className={`h-1 flex-1 rounded-full ${s <= step ? 'bg-slate-900' : 'bg-slate-100'}`}
                    />
                  ))}
                </div>

                {/* Step Content */}
                <div>
                  {step === 1 && (
                    <div className="space-y-3">
                      <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{t('mkt_basic_info', 'Basic Information')}</h3>
                      <input
                        type="text"
                        placeholder={t('mkt_campaign_title_placeholder', 'Campaign Title')}
                        aria-label={t('mkt_campaign_title', 'Campaign Title')}
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="w-full px-3 py-2 text-sm bg-slate-50/80 border-0 ring-1 ring-slate-200/60 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/20 placeholder-slate-400"
                      />
                      <select
                        aria-label={t('mkt_campaign_type', 'Campaign Type')}
                        value={formData.campaign_type}
                        onChange={(e) => setFormData({ ...formData, campaign_type: e.target.value })}
                        className="w-full px-3 py-2 text-sm bg-slate-50/80 border-0 ring-1 ring-slate-200/60 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/20 placeholder-slate-400"
                      >
                        <option value="">{t('mkt_select_type', 'Select Campaign Type')}</option>
                        <option value="sponsored_post">{t('mkt_type_sponsored', 'Sponsored Post')}</option>
                        <option value="product_placement">{t('mkt_type_product', 'Product Placement')}</option>
                        <option value="brand_ambassador">{t('mkt_type_ambassador', 'Brand Ambassador')}</option>
                        <option value="event">{t('mkt_type_event', 'Event')}</option>
                      </select>
                      <textarea
                        placeholder={t('mkt_campaign_desc_placeholder', 'Campaign Description')}
                        aria-label={t('mkt_campaign_description', 'Campaign Description')}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={3}
                        className="form-textarea w-full px-3 py-2 text-sm bg-slate-50/80 border-0 ring-1 ring-slate-200/60 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/20 resize-none placeholder-slate-400"
                      />
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-3">
                      <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{t('mkt_targeting', 'Targeting Criteria')}</h3>
                      <div>
                        <span className="text-xs font-medium text-slate-600 mb-2 block">{t('mkt_niches', 'Niches')}</span>
                        <div className="grid grid-cols-3 gap-1.5">
                          {['Fashion', 'Beauty', 'Fitness', 'Tech', 'Food', 'Travel'].map((niche) => {
                            const isChecked = formData.niches.includes(niche.toLowerCase());
                            return (
                              <button
                                key={niche}
                                type="button"
                                onClick={() => {
                                  const val = niche.toLowerCase();
                                  const nextNiches = isChecked
                                    ? formData.niches.filter(n => n !== val)
                                    : [...formData.niches, val];
                                  setFormData({ ...formData, niches: nextNiches });
                                }}
                                className={`px-2.5 py-1.5 rounded-full text-[11px] font-semibold transition-all ${
                                  isChecked
                                    ? 'bg-slate-900 text-white shadow-sm'
                                    : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                }`}
                              >
                                {niche}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="space-y-3">
                      <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{t('mkt_budget_payment', 'Budget & Payment')}</h3>
                      <input
                        type="number"
                        placeholder={t('mkt_total_budget_placeholder', 'Total Budget ($)')}
                        aria-label={t('mkt_total_budget', 'Total Budget ($)')}
                        value={formData.budget === 0 ? '' : formData.budget}
                        onChange={(e) => setFormData({ ...formData, budget: Number(e.target.value) })}
                        className="w-full px-3 py-2 text-sm bg-slate-50/80 border-0 ring-1 ring-slate-200/60 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/20 placeholder-slate-400"
                      />
                      <input
                        type="date"
                        placeholder={t('mkt_campaign_deadline_placeholder', 'Campaign Deadline')}
                        aria-label={t('mkt_campaign_deadline', 'Campaign Deadline')}
                        value={formData.deadline}
                        onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                        className="w-full px-3 py-2 text-sm bg-slate-50/80 border-0 ring-1 ring-slate-200/60 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/20 placeholder-slate-400"
                      />
                    </div>
                  )}

                  {step === 4 && (
                    <div className="space-y-3">
                      <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{t('mkt_deliverables', 'Deliverables')}</h3>
                      <textarea
                        placeholder={t('mkt_deliverables_placeholder', 'Define deliverables (e.g., 3 Instagram posts, 1 Reel)')}
                        aria-label={t('mkt_deliverables', 'Deliverables')}
                        value={formData.deliverables_description}
                        onChange={(e) => setFormData({ ...formData, deliverables_description: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 text-sm bg-slate-50/80 border-0 ring-1 ring-slate-200/60 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/20 placeholder-slate-400"
                      />
                    </div>
                  )}

                  {step === 5 && (
                    <div className="space-y-3">
                      <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{t('mkt_brand_assets', 'Brand Assets')}</h3>
                      <p className="text-xs text-slate-500">{t('mkt_upload_assets', 'Upload logos, product images, or brand guidelines')}</p>
                      <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center cursor-pointer hover:bg-slate-50 transition">
                        <Plus size={20} className="mx-auto text-slate-400 mb-1" />
                        <p className="text-xs text-slate-500">{t('mkt_drag_drop', 'Click to upload or drag and drop')}</p>
                      </div>
                    </div>
                  )}

                  {step === 6 && (
                    <div className="space-y-3">
                      <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{t('mkt_review_launch', 'Review & Launch')}</h3>
                      <div className="bg-slate-50/80 p-3 rounded-xl ring-1 ring-slate-100">
                        <p className="text-xs text-slate-600">
                          {t('mkt_ready_launch', 'You&apos;re all set! Review your campaign details and launch when ready.')}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Navigation */}
                <div className="flex gap-2 mt-5">
                  <button
                    onClick={() => setStep(Math.max(1, step - 1))}
                    disabled={step === 1}
                    className="flex-1 px-4 py-2.5 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition"
                  >
                    {t('mkt_back', 'Back')}
                  </button>
                  {step < 6 ? (
                    <button
                      onClick={() => setStep(Math.min(6, step + 1))}
                      className="flex-1 px-4 py-2.5 bg-slate-900 text-white rounded-xl text-xs font-semibold hover:bg-emerald-600 transition"
                    >
                      {t('mkt_next', 'Next')}
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        createCampaignMutation.mutate(formData);
                      }}
                      className="flex-1 px-4 py-2.5 bg-slate-900 text-white rounded-xl text-xs font-semibold hover:bg-emerald-600 transition"
                    >
                      {t('mkt_launch_campaign', 'Launch Campaign')}
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Campaign Detail Modal */}
      <AnimatePresence>
        {detailOpen && selectedCampaign && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-40"
              onClick={() => setDetailOpen(false)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Escape' || e.key === 'Enter') { setDetailOpen(false); } }}
              aria-label="Close campaign details"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="fixed inset-4 bg-white rounded-2xl z-50 max-w-3xl mx-auto max-h-[90vh] overflow-y-auto shadow-lg ring-1 ring-slate-200/50"
            >
              <div className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-sm font-semibold text-slate-900 mb-1">{selectedCampaign.title}</h2>
                    <p className="text-xs text-slate-500">{selectedCampaign.description}</p>
                  </div>
                  <button onClick={() => setDetailOpen(false)} className="p-1.5 hover:bg-slate-100 rounded-lg">
                    <X size={16} />
                  </button>
                </div>

                <div className="grid grid-cols-4 gap-2 mb-5 p-3 bg-slate-50/80 rounded-xl ring-1 ring-slate-100">
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{t('mkt_budget', 'Budget')}</p>
                    <p className="text-lg font-semibold text-slate-900">${((selectedCampaign.total_budget ?? 0) / 1000).toFixed(0)}K</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{t('mkt_applications', 'Applications')}</p>
                    <p className="text-lg font-semibold text-slate-900">{selectedCampaign.total_applications ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{t('mkt_accepted', 'Accepted')}</p>
                    <p className="text-lg font-semibold text-slate-900">{selectedCampaign.accepted_creators ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{t('mkt_deadline', 'Deadline')}</p>
                    <p className="text-sm font-semibold text-slate-900">
                      {selectedCampaign.application_deadline ? new Date(selectedCampaign.application_deadline).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>

                <h3 className="text-[13px] font-semibold text-slate-800 mb-2">{t('mkt_proposals', 'Proposals')} ({selectedCampaign.total_applications ?? 0})</h3>
                <div className="space-y-2 mb-5">
                  {[...Array(Math.min(3, selectedCampaign.total_applications ?? 0))].map((_, i) => (
                    <div key={i} className="p-3 bg-slate-50/80 rounded-xl ring-1 ring-slate-100 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-200 rounded-lg" />
                        <div>
                          <p className="text-xs font-semibold text-slate-900">{t('mkt_creator_name', 'Creator Name')}</p>
                          <p className="text-[11px] text-slate-500">{t('mkt_proposed', 'Proposed')}: $5,000</p>
                        </div>
                      </div>
                      <div className="flex gap-1.5">
                        <button className="px-2.5 py-1 bg-emerald-50 text-emerald-700 text-[10px] font-semibold rounded-lg hover:bg-emerald-100 transition">
                          {t('mkt_accept', 'Accept')}
                        </button>
                        <button className="px-2.5 py-1 bg-red-50 text-red-600 text-[10px] font-semibold rounded-lg hover:bg-red-100 transition">
                          {t('mkt_reject', 'Reject')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <h3 className="text-[13px] font-semibold text-slate-800 mb-2">{t('mkt_deliverables', 'Deliverables')}</h3>
                <div className="space-y-2">
                  {(selectedCampaign.deliverables || []).map((deliverable) => (
                    <div key={deliverable.id} className="p-3 bg-slate-50/80 rounded-xl ring-1 ring-slate-100">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-slate-900">{deliverable.title}</p>
                          <p className="text-[11px] text-slate-500">{deliverable.description}</p>
                        </div>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            deliverable.status === 'approved'
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-amber-50 text-amber-700'
                          }`}
                        >
                          {deliverable.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================================================
// MY PROPOSALS TAB
// ============================================================================

const MyProposalsTab: React.FC = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'sent' | 'received'>('sent');

  const { data: proposals = [], isLoading } = useQuery({
    queryKey: ['my-proposals', tab],
    queryFn: async () => {
      const response = await api.get('/marketplace/proposals', { params: { type: tab } });
      return (response.data as { items?: unknown[] }).items || response.data || [];
    },
  });

  const queryClient = useQueryClient();

  // Respond to proposal mutation (accept / reject / negotiate)
  const respondMutation = useMutation({
    mutationFn: async ({ proposalId, action, message }: { proposalId: string; action: 'accepted' | 'rejected' | 'negotiating'; message?: string }) => {
      const response = await api.put(`/marketplace/proposals/${proposalId}`, { status: action, message: message || '' });
      return response.data;
    },
    onSuccess: (_data, variables) => {
      toast.success(`Proposal ${variables.action} successfully`);
      void queryClient.invalidateQueries({ queryKey: ['my-proposals'] });
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, 'Failed to update proposal'));
    },
  });

  const getStatusColor = (status: Proposal['status']) => {
    const colors: Record<Proposal['status'], string> = {
      pending: 'bg-amber-100 text-amber-700',
      accepted: 'bg-emerald-100 text-emerald-700 font-bold',
      rejected: 'bg-red-100 text-red-700',
      negotiating: 'bg-indigo-100 text-indigo-700',
      completed: 'bg-slate-100 text-slate-700',
    };
    return colors[status];
  };

  return (
    <div className="space-y-4">
      {/* Tab Selector */}
      <div className="flex items-center gap-0.5">
        <button
          onClick={() => setTab('sent')}
          className={`inline-flex items-center px-3 py-1.5 text-[11px] font-semibold rounded-full transition-all ${
            tab === 'sent'
              ? 'bg-slate-900 text-white shadow-sm'
              : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
          }`}
        >
          {t('mkt_sent_proposals', 'Sent Proposals')}
        </button>
        <button
          onClick={() => setTab('received')}
          className={`inline-flex items-center px-3 py-1.5 text-[11px] font-semibold rounded-full transition-all ${
            tab === 'received'
              ? 'bg-slate-900 text-white shadow-sm'
              : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
          }`}
        >
          {t('mkt_received_proposals', 'Received Proposals')}
        </button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-slate-100 rounded-2xl h-24 animate-pulse" />
          ))}
        </div>
      )}

      {/* Proposal Cards */}
      {!isLoading && proposals.length > 0 && (
        <div className="space-y-3">
          {proposals.map((proposal: Proposal) => (
            <div
              key={proposal.id}
              className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/50 hover:ring-slate-300 transition-all p-4 flex flex-col md:flex-row items-center justify-between gap-4"
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center ring-1 ring-slate-100 shrink-0">
                    {tab === 'sent' ? <Target size={16} className="text-emerald-500" /> : <Users size={16} className="text-emerald-500" />}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <h3 className="text-xs font-semibold text-slate-900 truncate">
                      {tab === 'sent'
                        ? proposal.campaign?.title
                        : proposal.creator?.displayName}
                    </h3>
                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider ${getStatusColor(proposal.status)}`}>
                      {proposal.status}
                    </span>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    {tab === 'sent'
                      ? `Proposed Offer: $${proposal.proposedPrice.toLocaleString()}`
                      : proposal.campaign?.title}
                  </p>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="flex items-center gap-1 text-[10px] text-slate-400">
                        <Clock size={10} />
                        {proposal.timeline}
                    </span>
                    <span className="flex items-center gap-1 text-[10px] text-slate-400">
                        <Calendar size={10} />
                        {new Date(proposal.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 w-full md:w-auto pt-3 md:pt-0 border-t md:border-t-0 border-slate-100/80">
                <div className="text-center md:text-right">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5">{t('mkt_amount', 'Amount')}</p>
                  <p className="text-sm font-semibold text-slate-900">
                    ${proposal.proposedPrice.toLocaleString()}
                  </p>
                </div>

                <div className="flex gap-1.5 shrink-0">
                    {proposal.status === 'pending' && tab === 'received' && (
                      <>
                        <button
                          onClick={() => respondMutation.mutate({ proposalId: proposal.id, action: 'accepted' })}
                          disabled={respondMutation.isPending}
                          className="px-3 py-1.5 bg-slate-900 text-white text-[10px] font-semibold rounded-lg hover:bg-emerald-600 transition disabled:opacity-50"
                        >
                          {t('mkt_accept', 'Accept')}
                        </button>
                        <button
                          onClick={() => respondMutation.mutate({ proposalId: proposal.id, action: 'negotiating', message: 'Let\u0027s discuss terms.' })}
                          disabled={respondMutation.isPending}
                          className="px-3 py-1.5 bg-slate-100 text-slate-700 text-[10px] font-semibold rounded-lg hover:bg-slate-200 transition disabled:opacity-50"
                        >
                          {t('mkt_negotiate', 'Negotiate')}
                        </button>
                        <button
                          onClick={() => respondMutation.mutate({ proposalId: proposal.id, action: 'rejected' })}
                          disabled={respondMutation.isPending}
                          className="px-3 py-1.5 bg-red-50 text-red-600 text-[10px] font-semibold rounded-lg hover:bg-red-100 transition disabled:opacity-50"
                        >
                          {t('mkt_reject', 'Reject')}
                        </button>
                      </>
                    )}
                    {(proposal.status === 'negotiating' || proposal.status === 'accepted') && (
                      <button
                        onClick={() => toast.success('Unified inbox integration coming soon')}
                        className="px-3 py-1.5 bg-slate-900 text-white text-[10px] font-semibold rounded-lg hover:bg-slate-800 transition flex items-center gap-1.5"
                      >
                        <MessageCircle size={11} />
                        {t('mkt_chat', 'Chat')}
                      </button>
                    )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty */}
      {!isLoading && proposals.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/50">
          <MessageCircle size={32} className="text-slate-300 mb-3" />
          <h3 className="text-[13px] font-semibold text-slate-800 mb-1">
            {t('mkt_no_proposals', 'No')} {tab} {t('mkt_proposals_yet', 'proposals yet')}
          </h3>
          <p className="text-xs text-slate-500 max-w-xs text-center">
            {tab === 'sent' ? t('mkt_start_sending', 'Discover creators and start sending collaboration proposals today.') : t('mkt_creators_send', 'When creators apply to your campaigns, you will see their proposals here.')}
          </p>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// REVIEWS & REPUTATION TAB
// ============================================================================

const ReviewsReputationTab: React.FC = () => {
  const { t } = useTranslation();
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['marketplace-stats'],
    queryFn: async () => {
      const response = await api.get('/marketplace/stats');
      return response.data;
    },
  });

  const { data: reviews = [], isLoading: reviewsLoading } = useQuery({
    queryKey: ['my-reviews'],
    queryFn: async () => {
      const response = await api.get('/marketplace/reviews');
      return response.data;
    },
  });

  if (statsLoading) {
    return <div className="bg-slate-100 rounded-2xl h-48 animate-pulse" />;
  }

  return (
    <div className="space-y-4">
      {/* Rating Overview */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/50 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Award size={14} className="text-emerald-500" />
          <span className="text-[13px] font-semibold text-slate-800">{t('mkt_your_reputation', 'Your Reputation')}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="flex flex-col items-center justify-center p-4 bg-slate-50/80 rounded-xl ring-1 ring-slate-100">
            <div className="text-3xl font-semibold text-slate-900 mb-1">
              {stats?.averageRating?.toFixed(1) || '0.0'}
            </div>
            <RatingStars rating={stats?.averageRating || 0} size={14} showNumber={false} />
            <p className="text-[10px] font-semibold text-slate-400 mt-2 uppercase tracking-wider">{stats?.reviewCount || 0} {t('mkt_reviews', 'Reviews')}</p>
          </div>

          <div className="md:col-span-3 flex flex-col justify-center">
            <div className="space-y-3">
              {[
                { label: t('mkt_communication', 'Communication'), score: 4.8 },
                { label: t('mkt_delivery_quality', 'Delivery Quality'), score: 4.6 },
                { label: t('mkt_professionalism', 'Professionalism'), score: 4.9 },
              ].map((category) => (
                <div key={category.label}>
                  <div className="flex justify-between text-[11px] mb-1">
                    <span className="text-slate-500">{category.label}</span>
                    <span className="font-semibold text-slate-900">{category.score}/5.0</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                    <div
                      className="bg-emerald-500 h-full rounded-full transition-all duration-700"
                      style={{ width: `${(category.score / 5) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Reviews */}
      <div>
        <div className="flex items-center gap-2 mb-3">
            <span className="text-[13px] font-semibold text-slate-800">{t('mkt_recent_reviews', 'Community Feedback')}</span>
            <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-semibold rounded">{reviews.length}</span>
        </div>

        {reviewsLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="bg-slate-100 rounded-2xl h-36 animate-pulse" />
            ))}
          </div>
        )}

        {!reviewsLoading && reviews.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {reviews.map((review: Review) => (
              <div
                key={review.id}
                className="bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/50 hover:ring-slate-300 transition-all p-4"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <img
                      src={review.reviewer?.avatar}
                      alt={review.reviewer?.displayName}
                      className="w-9 h-9 rounded-lg object-cover"
                    />
                    <div>
                      <p className="text-xs font-semibold text-slate-900">{review.reviewer?.displayName}</p>
                      <RatingStars rating={review.rating} size={10} showNumber={false} />
                    </div>
                  </div>
                  <span className="text-[10px] text-slate-400">
                    {new Date(review.createdAt).toLocaleDateString()}
                  </span>
                </div>

                <h3 className="text-xs font-semibold text-slate-900 mb-1">{review.title}</h3>
                <p className="text-[11px] text-slate-600 leading-relaxed mb-3 line-clamp-2">{review.content}</p>

                <div className="grid grid-cols-3 gap-1 p-2 bg-slate-50/80 rounded-lg text-[10px]">
                  <div className="text-center">
                    <p className="text-slate-400 font-semibold uppercase mb-0.5">{t('mkt_comm', 'Comm')}</p>
                    <p className="font-semibold text-slate-900">{review.categories.communication}/5</p>
                  </div>
                  <div className="text-center border-x border-slate-200">
                    <p className="text-slate-400 font-semibold uppercase mb-0.5">{t('mkt_quality', 'Quality')}</p>
                    <p className="font-semibold text-slate-900">{review.categories.delivery}/5</p>
                  </div>
                  <div className="text-center">
                    <p className="text-slate-400 font-semibold uppercase mb-0.5">{t('mkt_pro', 'Pro')}</p>
                    <p className="font-semibold text-slate-900">{review.categories.professionalism}/5</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!reviewsLoading && reviews.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 bg-white rounded-2xl shadow-sm ring-1 ring-slate-200/50">
            <Award size={32} className="text-slate-300 mb-3" />
            <h3 className="text-[13px] font-semibold text-slate-800 mb-1">{t('mkt_no_reviews_yet', 'Begin your legacy')}</h3>
            <p className="text-xs text-slate-500 max-w-xs text-center">{t('mkt_no_reviews_desc', 'Complete your first successful collaboration and earn your first community review.')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN MARKETPLACE PAGE
// ============================================================================


// (MarketplaceHero removed — using compact toolbar instead.)

// ============================================================================
// MAIN MARKETPLACE PAGE
// ============================================================================

export const MarketplacePage: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  const [activeTab, setActiveTab] = useState<'discover' | 'campaigns' | 'proposals' | 'reviews'>('discover');
  const [searchQuery, setSearchQuery] = useState('');

  const tabs = [
    { id: 'discover', label: t('mkt_discover_creators', 'Discover Creators'), icon: Users },
    { id: 'campaigns', label: t('mkt_my_campaigns', 'My Campaigns'), icon: Target },
    { id: 'proposals', label: t('mkt_my_proposals', 'My Proposals'), icon: MessageCircle },
    { id: 'reviews', label: t('mkt_reviews_reputation', 'Reviews & Reputation'), icon: Award },
  ];

  return (
    <div className="bg-slate-50/80 min-h-0">

      {/* ─── Page Toolbar ─── */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200/50 px-6 md:px-8 py-2.5">
        <div className="flex items-center justify-between max-w-[1440px] mx-auto">
          {/* Tab Navigation */}
          <nav className="flex items-center gap-0.5 overflow-x-auto scrollbar-hide" aria-label="Marketplace tabs">
            {tabs.map((tab) => {
              const TabIcon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as typeof activeTab)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-full whitespace-nowrap transition-all ${
                    isActive
                      ? 'bg-slate-900 text-white shadow-sm'
                      : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                  }`}
                >
                  <TabIcon size={12} />
                  {tab.label}
                </button>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            {/* Search (inline, compact) */}
            {activeTab === 'discover' && (
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder={t('mkt_search_placeholder_hero', 'Search creators...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 pr-3 py-1.5 text-xs bg-slate-50/80 border-0 ring-1 ring-slate-200/60 rounded-lg focus:bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/20 placeholder-slate-400 w-48 md:w-64 transition"
                />
              </div>
            )}
            <button
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold rounded-full transition-all text-slate-500 hover:text-slate-700 hover:bg-slate-100"
              style={{ color: theme.hex?.primary }}
            >
              <Plus size={12} />
              {t('mkt_post_campaign', 'Post Campaign')}
            </button>
          </div>
        </div>
      </div>

      {/* ─── Main Content ─── */}
      <div className="px-4 md:px-8 py-4">
        <div className="max-w-[1440px] mx-auto">
          {activeTab === 'discover' && <DiscoverCreatorsTab searchQuery={searchQuery} />}
          {activeTab === 'campaigns' && <MyCampaignsTab />}
          {activeTab === 'proposals' && <MyProposalsTab />}
          {activeTab === 'reviews' && <ReviewsReputationTab />}
        </div>
      </div>
    </div>
  );
};

export default MarketplacePage;
